/**
 * usePlaybookDraft — local, unsaved edits to one agent's playbook, one draft per rule scope.
 *
 * A playbook has a document per rule scope: the landlord's all-properties rules (`'landlord'`) and
 * one per property that has its own rules (keyed by property id). Each scope has its own draft,
 * its own change count and its own save body; `anyDirty` spans them so one unsaved-changes guard
 * covers the whole page.
 *
 * Nothing here talks to the server: the page sends a scope's `toSaveBody()` on that group's save.
 * The reducer (`playbookDraftReducer`) is pure and exported for unit tests. `changeCount` is a diff
 * against the saved document, so undoing an edit by hand brings the scope back to clean. A scope's
 * draft is replaced when its saved document changes version (after that scope's save); a save of
 * one group never discards another group's unsaved edits.
 */

import { useCallback, useMemo, useReducer, useState } from 'react';
import type {
  AgentPlaybookKindView,
  PlaybookDefaultView,
  PlaybookRuleView,
  SaveAgentPlaybookBody,
} from '@resida/api-types/agent';

/** `'landlord'` for the all-properties document, else a property id. */
export type PlaybookScopeKey = string;
export const LANDLORD_SCOPE: PlaybookScopeKey = 'landlord';

/** A rule in the draft. `key` is the saved id, or a local id for a rule not saved yet. */
export interface DraftRule {
  key: string;
  /** Saved id; absent for a rule added in this draft. */
  id?: string;
  text: string;
  /** Where the saved rule came from; absent for a rule added in this draft. Never sent on save. */
  source?: PlaybookRuleView['source'];
}

export interface PlaybookDraft {
  defaults: PlaybookDefaultView[];
  rules: DraftRule[];
}

/** The saved state of one scope's document, as the draft diffs against it. */
export interface ScopeDoc {
  defaults: PlaybookDefaultView[];
  rules: PlaybookRuleView[];
  versionId: string | null;
}

/** A property whose rules the landlord is about to create (no document yet). */
export interface NewPropertyScope {
  propertyId: string;
  propertyName: string;
}

export type PlaybookDraftAction =
  | { type: 'reset'; doc: ScopeDoc }
  | { type: 'toggleDefault'; itemId: string; catalogTitle: string; inheritedOff: boolean }
  | { type: 'rewordDefault'; itemId: string; text: string; catalogTitle: string }
  | { type: 'resetDefault'; itemId: string }
  | { type: 'removeRetired'; itemId: string }
  | { type: 'addRule'; text: string; key: string }
  | { type: 'editRule'; key: string; text: string }
  | { type: 'removeRule'; key: string }
  | { type: 'moveRuleUp'; key: string };

export function draftFromDoc(doc: Pick<ScopeDoc, 'defaults' | 'rules'>): PlaybookDraft {
  return {
    defaults: doc.defaults.map((d) => ({ ...d })),
    rules: doc.rules.map((r) => ({ key: r.id, id: r.id, text: r.text, source: r.source })),
  };
}

export function playbookDraftReducer(draft: PlaybookDraft, action: PlaybookDraftAction): PlaybookDraft {
  switch (action.type) {
    case 'reset':
      return draftFromDoc(action.doc);
    case 'toggleDefault': {
      const current = draft.defaults.find((d) => d.itemId === action.itemId);
      // Whether the default applies at this scope: its own state, else what the scope inherits.
      const appliesNow = current ? current.state !== 'off' : !action.inheritedOff;
      const without = draft.defaults.filter((d) => d.itemId !== action.itemId);
      if (appliesNow && !action.inheritedOff) {
        const off: PlaybookDefaultView = {
          itemId: action.itemId,
          state: 'off',
          baseTitle: current?.baseTitle ?? action.catalogTitle,
          reworded: false,
          retired: false,
        };
        return { ...draft, defaults: current ? draft.defaults.map((d) => (d.itemId === action.itemId ? off : d)) : [...draft.defaults, off] };
      }
      if (appliesNow) return { ...draft, defaults: without };
      // Off now. Where nothing is inherited, removing the customization turns it back on; where
      // the all-properties rules turned it off, this scope turns it on explicitly.
      if (!action.inheritedOff) return { ...draft, defaults: without };
      const on: PlaybookDefaultView = {
        itemId: action.itemId,
        state: 'on',
        baseTitle: current?.baseTitle ?? action.catalogTitle,
        reworded: false,
        retired: false,
      };
      return { ...draft, defaults: [...without, on] };
    }
    case 'rewordDefault': {
      const text = action.text.trim();
      const others = draft.defaults.filter((d) => d.itemId !== action.itemId);
      // Empty or identical to Resida's wording means "no customization".
      if (text === '' || text === action.catalogTitle) return { ...draft, defaults: others };
      return {
        ...draft,
        defaults: [
          ...others,
          { itemId: action.itemId, state: 'rewritten', text, baseTitle: action.catalogTitle, reworded: false, retired: false },
        ],
      };
    }
    case 'resetDefault':
    case 'removeRetired':
      return { ...draft, defaults: draft.defaults.filter((d) => d.itemId !== action.itemId) };
    case 'addRule': {
      const text = action.text.trim();
      if (text === '') return draft;
      return { ...draft, rules: [...draft.rules, { key: action.key, text }] };
    }
    case 'editRule':
      return {
        ...draft,
        rules: draft.rules.map((r) => (r.key === action.key ? { ...r, text: action.text.trim() } : r)),
      };
    case 'removeRule':
      return { ...draft, rules: draft.rules.filter((r) => r.key !== action.key) };
    case 'moveRuleUp': {
      const i = draft.rules.findIndex((r) => r.key === action.key);
      if (i <= 0) return draft;
      const rules = [...draft.rules];
      [rules[i - 1], rules[i]] = [rules[i]!, rules[i - 1]!];
      return { ...draft, rules };
    }
  }
}

const sameDefault = (a: PlaybookDefaultView, b: PlaybookDefaultView) =>
  a.state === b.state && (a.text ?? '') === (b.text ?? '');

/** Number of distinct edits between a saved document and its draft. */
export function countPlaybookChanges(doc: Pick<ScopeDoc, 'defaults' | 'rules'>, draft: PlaybookDraft): number {
  let changes = 0;

  const saved = new Map(doc.defaults.map((d) => [d.itemId, d]));
  const drafted = new Map(draft.defaults.map((d) => [d.itemId, d]));
  for (const [itemId, d] of drafted) {
    const before = saved.get(itemId);
    if (!before || !sameDefault(before, d)) changes += 1;
  }
  for (const itemId of saved.keys()) if (!drafted.has(itemId)) changes += 1;

  const savedRules = new Map(doc.rules.map((r) => [r.id, r.text]));
  const keptIds: string[] = [];
  for (const rule of draft.rules) {
    if (rule.id === undefined) changes += 1;
    else {
      keptIds.push(rule.id);
      if (savedRules.get(rule.id) !== rule.text) changes += 1;
    }
  }
  for (const id of savedRules.keys()) if (!keptIds.includes(id)) changes += 1;
  // Reordering the rules that were kept counts as one change.
  const savedOrder = doc.rules.map((r) => r.id).filter((id) => keptIds.includes(id));
  if (savedOrder.some((id, i) => id !== keptIds[i])) changes += 1;

  return changes;
}

/**
 * The request body for saving one scope. Rules are exactly `{ id?, text }` — `source` and
 * `conversationId` are never sent (the request schema is strict, BR-PB-ENVELOPE). The all-properties
 * body carries no `ruleScope` key (BR-SETTINGS-AGENTS-BASELINE); a property's carries its id.
 */
export function playbookDraftToSaveBody(draft: PlaybookDraft, scope: PlaybookScopeKey = LANDLORD_SCOPE): SaveAgentPlaybookBody {
  return {
    ...(scope === LANDLORD_SCOPE ? {} : { ruleScope: { kind: 'property' as const, propertyId: scope } }),
    defaults: draft.defaults.map((d) =>
      d.state === 'rewritten'
        ? { itemId: d.itemId, state: 'rewritten' as const, text: d.text }
        : { itemId: d.itemId, state: d.state },
    ),
    rules: draft.rules.map((r) => (r.id ? { id: r.id, text: r.text } : { text: r.text })),
  };
}

interface DraftsState {
  drafts: Record<PlaybookScopeKey, PlaybookDraft>;
  versions: Record<PlaybookScopeKey, string | null>;
}

type DraftsAction =
  | { type: 'sync'; docs: Record<PlaybookScopeKey, ScopeDoc> }
  | { type: 'scope'; scope: PlaybookScopeKey; action: PlaybookDraftAction };

function draftsReducer(state: DraftsState, action: DraftsAction): DraftsState {
  if (action.type === 'scope') {
    const draft = state.drafts[action.scope];
    if (!draft) return state;
    return { ...state, drafts: { ...state.drafts, [action.scope]: playbookDraftReducer(draft, action.action) } };
  }
  // Keep a scope's draft while its saved version is unchanged; replace it when the version moved
  // (that scope was saved) or the scope is new; drop scopes that no longer exist.
  const drafts: DraftsState['drafts'] = {};
  const versions: DraftsState['versions'] = {};
  for (const [scope, doc] of Object.entries(action.docs)) {
    const existing = state.drafts[scope];
    const unchanged = existing !== undefined && scope in state.versions && state.versions[scope] === doc.versionId;
    drafts[scope] = unchanged ? existing : draftFromDoc(doc);
    versions[scope] = doc.versionId;
  }
  return { drafts, versions };
}

const initialDraftsState = (docs: Record<PlaybookScopeKey, ScopeDoc>): DraftsState =>
  draftsReducer({ drafts: {}, versions: {} }, { type: 'sync', docs });

let localKeyCounter = 0;
const newRuleKey = () => `new-${(localKeyCounter += 1)}`;

/** The saved documents of a playbook view: the landlord's, each property's, and not-yet-created ones. */
export function scopeDocsOf(
  view: AgentPlaybookKindView,
  added: readonly NewPropertyScope[],
): Record<PlaybookScopeKey, ScopeDoc> {
  const docs: Record<PlaybookScopeKey, ScopeDoc> = {
    [LANDLORD_SCOPE]: { defaults: view.defaults, rules: view.rules, versionId: view.versionId },
  };
  for (const scope of view.propertyScopes) {
    docs[scope.propertyId] = { defaults: scope.defaults, rules: scope.rules, versionId: scope.versionId };
  }
  for (const { propertyId } of added) {
    if (!(propertyId in docs)) docs[propertyId] = { defaults: [], rules: [], versionId: null };
  }
  return docs;
}

export function usePlaybookDraft(view: AgentPlaybookKindView, added: readonly NewPropertyScope[] = []) {
  const docs = useMemo(() => scopeDocsOf(view, added), [view, added]);
  const [state, dispatch] = useReducer(draftsReducer, docs, initialDraftsState);

  // A new saved view replaces a scope's draft only if that scope's version moved. Done during
  // render so a dirty flag never flashes between the save landing and the draft catching up.
  const [baseDocs, setBaseDocs] = useState(docs);
  if (docs !== baseDocs) {
    setBaseDocs(docs);
    dispatch({ type: 'sync', docs });
  }

  const catalogTitle = useCallback(
    (itemId: string) => view.items.find((i) => i.id === itemId)?.title ?? '',
    [view.items],
  );

  const scopeApi = (scope: PlaybookScopeKey) => {
    const doc = docs[scope]!;
    const draft = state.drafts[scope] ?? draftFromDoc(doc);
    const changeCount = countPlaybookChanges(doc, draft);
    const send = (action: PlaybookDraftAction) => dispatch({ type: 'scope', scope, action });
    // A property scope inherits the all-properties customizations; "off elsewhere" is read from the saved landlord document.
    const inheritedOff = (itemId: string) =>
      scope !== LANDLORD_SCOPE && docs[LANDLORD_SCOPE]!.defaults.some((d) => d.itemId === itemId && d.state === 'off');
    return {
      draft,
      changeCount,
      isDirty: changeCount > 0,
      toggleDefault: (itemId: string) =>
        send({ type: 'toggleDefault', itemId, catalogTitle: catalogTitle(itemId), inheritedOff: inheritedOff(itemId) }),
      rewordDefault: (itemId: string, text: string) =>
        send({ type: 'rewordDefault', itemId, text, catalogTitle: catalogTitle(itemId) }),
      resetDefault: (itemId: string) => send({ type: 'resetDefault', itemId }),
      removeRetired: (itemId: string) => send({ type: 'removeRetired', itemId }),
      addRule: (text: string) => send({ type: 'addRule', text, key: newRuleKey() }),
      editRule: (key: string, text: string) => send({ type: 'editRule', key, text }),
      removeRule: (key: string) => send({ type: 'removeRule', key }),
      moveRuleUp: (key: string) => send({ type: 'moveRuleUp', key }),
      discard: () => send({ type: 'reset', doc }),
      toSaveBody: () => playbookDraftToSaveBody(draft, scope),
    };
  };

  // Unsaved changes per scope with edits (a scope never touched has no draft and no changes).
  const changeCounts = Object.keys(docs).map((scope) => ({
    scope,
    count: state.drafts[scope] === undefined ? 0 : countPlaybookChanges(docs[scope]!, state.drafts[scope]!),
  }));
  const totalChangeCount = changeCounts.reduce((sum, c) => sum + c.count, 0);
  const anyDirty = totalChangeCount > 0;

  /** Drop every scope's unsaved edits (the page-level Discard). */
  const discardAll = () => {
    for (const { scope, count } of changeCounts) {
      if (count > 0) dispatch({ type: 'scope', scope, action: { type: 'reset', doc: docs[scope]! } });
    }
  };

  return { scope: scopeApi, anyDirty, totalChangeCount, discardAll };
}
